// src/pages/team/TeamDocumentsTab.tsx
// Documents tab for the team workspace — tree/grid/list views with create button.

import { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Badge,
  ErrorAlert,
} from '@react/components';
import { useScope } from '@/hooks/useScope';
import { DocumentTreeView } from '@/components/DocumentTreeView';
import { DocumentGridView } from '@/components/DocumentGridView';

import type { DocumentTreeNode } from '@/features/document/types';
import {
  buildDocumentTree,
  filterDocuments,
  sortDocumentsByActivity,
  getEntityId,
  getDocumentStatus,
  getDocumentOwner,
  getStatusMapping,
  documentStatusMap,
  formatRelativeTime,
  getInitials,
  getAvatarColor,
  computeDocumentStats,
  getDocumentIcon,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// View mode
// ---------------------------------------------------------------------------

type ViewMode = 'tree' | 'grid' | 'list';

function ViewModeSwitch({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  const modes: { key: ViewMode; label: string; icon: React.ReactNode }[] = [
    {
      key: 'tree',
      label: 'Tree',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 3h7v7H3zM14 3h7v4H14zM14 10h7v4H14zM14 17h7v4H14z" />
        </svg>
      ),
    },
    {
      key: 'grid',
      label: 'Grid',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
        </svg>
      ),
    },
    {
      key: 'list',
      label: 'List',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex items-center border border-border rounded-lg overflow-hidden">
      {modes.map(m => (
        <button
          key={m.key}
          onClick={() => onChange(m.key)}
          className={`p-2 transition-colors ${
            mode === m.key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          title={m.label}
        >
          {m.icon}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search input
// ---------------------------------------------------------------------------

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative">
      <svg
        width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
      >
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2 text-sm bg-transparent border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30 transition-colors"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List row
// ---------------------------------------------------------------------------

function DocumentListRow({ document, locale, onClick }: { document: any; locale: string; onClick: () => void }) {
  const { t } = useI18n();
  if (!document) return null;
  const status = getDocumentStatus(document);
  const owner = getDocumentOwner(document);
  const statusInfo = getStatusMapping(documentStatusMap, status);
  const iconPath = getDocumentIcon(document);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-3 px-4 py-3 w-full text-left rounded-lg transition-colors hover:bg-muted/50"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground flex-shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
      </svg>

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate block">
          {document.title || t('common.untitled')}
        </span>
      </div>

      {status && (
        <Badge variant={statusInfo.variant as any} size="sm">
          {t(statusInfo.labelKey)}
        </Badge>
      )}

      {owner && (
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: getAvatarColor(owner) }}
          title={owner}
        >
          {getInitials(owner)}
        </div>
      )}

      {document.updated_at && (
        <span className="text-xs text-muted-foreground w-20 text-right flex-shrink-0 hidden sm:block">
          {formatRelativeTime(document.updated_at, locale)}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Stats bar
// ---------------------------------------------------------------------------

function StatsBar({ docs, t }: { docs: any[]; t: (k: string) => string }) {
  const stats = useMemo(() => computeDocumentStats(docs), [docs]);
  if (stats.total === 0) return null;

  return (
    <div className="flex items-center gap-3">
      <Badge variant="secondary" size="sm">{stats.total}</Badge>
      {stats.draft > 0 && <Badge variant="secondary" size="sm">{stats.draft} {t('document.status.draft')}</Badge>}
      {stats.published > 0 && <Badge variant="success" size="sm">{stats.published} {t('document.status.published')}</Badge>}
      {stats.inReview > 0 && <Badge variant="warning" size="sm">{stats.inReview} {t('document.status.inReview')}</Badge>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DocumentsSkeleton() {
  return (
    <div className="space-y-1">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
          <div className="w-5 h-5 rounded bg-muted flex-shrink-0" />
          <div className="h-4 rounded bg-muted flex-1" style={{ maxWidth: `${40 + Math.random() * 40}%` }} />
          <div className="h-5 w-16 rounded-full bg-muted flex-shrink-0" />
          <div className="h-4 w-12 rounded bg-muted flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ onCreate, t }: { onCreate: () => void; t: (k: string) => string }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
      <p className="text-sm font-medium text-foreground">{t('team.documents.empty')}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t('team.documents.emptyDescription')}</p>
      <Button size="sm" className="mt-4" onClick={onCreate}>
        <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {t('document.newPage')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const TeamDocumentsTab = observer(({ teamId }: { teamId: string }) => {
  const store = useDocument();
  const { t, locale } = useI18n();
  const scope = useScope();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [search, setSearch] = useState('');
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  // Load documents scoped to this team
  useEffect(() => {
    store.ListDocuments({
      pagination: { cursor: '', limit: 500, direction: 'forward' as const },
      sort: [],
      view: 0,
      date_range: {} as any,
      filters: {
        parent_type: 'team',
        parent_id: teamId,
      } as any,
    });
  }, [scope.productId, teamId, store]);

  // The list endpoint ignores ad-hoc filter params — scope to this team
  // client-side by the parent reference so other teams' documents never leak in.
  const documents: any[] = useMemo(
    () => (store.items ?? []).filter((d: any) => d.parent?.id === teamId),
    [store.items, teamId],
  );

  // Filter and sort
  const processedDocs = useMemo(() => {
    const filtered = filterDocuments(documents, search, 'all');
    return sortDocumentsByActivity(filtered);
  }, [documents, search]);

  // Build tree for tree view
  const tree = useMemo<DocumentTreeNode[]>(
    () => buildDocumentTree(processedDocs),
    [processedDocs],
  );

  const handleCreate = useCallback(
    () => navigate(`/documents/new?scope_type=team&scope_id=${teamId}`),
    [navigate, teamId],
  );

  const handleOpen = useCallback(
    (id: string) => navigate(`/documents/${id}`),
    [navigate],
  );

  const handleSelect = useCallback((id: string) => setSelectedDocId(id), []);

  // Error
  if (store.error && !store.isLoading) {
    return (
      <ErrorAlert
        error={store.error}
        dismissible
        onDismiss={() => store.clearError()}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('document.searchPlaceholder')}
          />
        </div>
        <div className="flex items-center gap-2">
          <ViewModeSwitch mode={viewMode} onChange={setViewMode} />
          <Button size="sm" onClick={handleCreate}>
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('document.newPage')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {!store.isLoading && processedDocs.length > 0 && (
        <StatsBar docs={processedDocs} t={t} />
      )}

      {/* Content */}
      {store.isLoading && documents.length === 0 ? (
        <DocumentsSkeleton />
      ) : processedDocs.length === 0 && !search ? (
        <EmptyState onCreate={handleCreate} t={t} />
      ) : processedDocs.length === 0 && search ? (
        <div className="py-12 text-center">
          <p className="text-sm text-muted-foreground">{t('document.noSearchResults')}</p>
        </div>
      ) : viewMode === 'tree' ? (
        <DocumentTreeView
          tree={tree}
          selectedDocId={selectedDocId ?? undefined}
          onSelect={handleSelect}
          onOpen={handleOpen}
        />
      ) : viewMode === 'grid' ? (
        <DocumentGridView
          documents={processedDocs}
          selectedDocId={selectedDocId ?? undefined}
          onSelect={handleSelect}
          onOpen={handleOpen}
        />
      ) : (
        <div className="space-y-0.5">
          {processedDocs.map(doc => (
            <DocumentListRow
              key={getEntityId(doc)}
              document={doc}
              locale={locale}
              onClick={() => handleOpen(getEntityId(doc))}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default TeamDocumentsTab;
